
/**
 * Module dependencies.
 */

import { AmbientProps } from 'src/types/ambients';
import { getHrefLangs } from 'src/core/utils/href-langs';
import { ifProp } from 'styled-tools';
import { useRouter } from 'next/router';
import FiltersListProvider from 'src/context/filters-list/provider';
import ListSection from 'src/components/inspiration/list-section';
import Loading from 'src/components/core/loading';
import Metatags from 'src/components/metatags';
import ModulesBuilder, { Module } from 'src/components/modules/modules-builder';
import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usePage from 'src/api/app/pages/use-page';

/**
 * `PageProps` type.
 */

type PageProps = {
  descriptionSeo?: string | null,
  id: string,
  modules: Module[],
  slug: string,
  subtitle: string,
  title: string,
  titleSeo?: string | null,
  translations: Record<string, unknown>[]
};

/**
 * Export `Props` type.
 */

export type Props = {
  ambients: {
    pageParams: number[],
    pages: Array<{
      data: AmbientProps[],
      nextPage: number | null,
      total: number
    }>
  },
  data: PageProps,
  isKiosk?: boolean,
  slug: string
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isLoading: boolean }>`
  position: relative;

  ${ifProp('isLoading', css`
    height: 100vh
  `)}
`;

/**
 * `InspirationPage` component.
 */

const InspirationPage = (props: Props): ReactElement => {
  const { ambients, isKiosk } = props;
  const { query } = useRouter();
  const { data, isLoading, isSuccess } = usePage<PageProps>({
    initialData: props.data,
    slug: props.slug
  });

  useNavbarTheme({ theme: 'light' });

  return (
    <FiltersListProvider>
      <>
        <Metatags
          description={data?.descriptionSeo}
          hrefLangs={getHrefLangs(data?.translations, 'inspiration')}
          isKiosk={isKiosk}
          pageTitle={data?.title}
          title={data?.titleSeo}
        />

        <Wrapper isLoading={isLoading}>
          <Loading active={isLoading} />

          {isSuccess && (
            <>
              <ListSection
                filterParams={query}
                initialData={ambients}
                isKiosk={isKiosk}
                subtitle={data?.subtitle}
                title={data?.title}
              />

              {!isKiosk && (
                <ModulesBuilder
                  allowed={['tools']}
                  modules={data?.modules}
                />
              )}
            </>
          )}
        </Wrapper>
      </>
    </FiltersListProvider>
  );
};

/**
 * Export `InspirationPage` component.
 */

export default InspirationPage;
